/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */

"use client";

import dayjs from "dayjs";
import { useSearchParams } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import {
  Dropdown,
  DropdownItem,
  TextFieldSts,
  Button,
  InlineInfoBox,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import ButtonGroup from "@/components/atoms/button-group/button-group";
import InputValidationWrapper from "@/components/atoms/input-validation-wrapper/input-validation-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import withApplication from "@/components/with-application";
import withBrand from "@/components/with-brand";
import {
  Individual,
  OrganizerEdit,
  QuestionThree,
  TravellerEdit,
} from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP, MAX_ADDRESS_LENGTH } from "@/constants";
import {
  useAddressApi,
  useFetchAddressData,
  useFetchNationalityData,
} from "@/hooks/api/address-api/address-api";
import useDebounce from "@/hooks/debounce/debounce";
import useInputFocus from "@/hooks/use-input-focus/use-input-focus";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { RootState } from "@/store/store";
import { AddressObj, AddressType } from "@/types/AddressTypes";
import { TitleKey } from "@/types/Organiser";
import {
  FormDataTypeOrganiser,
  OrganiserFormProps,
} from "@/types/OrganiserType";
import { showMessenger } from "@/utils/cxone.utils";
import { reorderNationalities } from "@/utils/filtered-nationality.utils";
import {
  answersQuestionYesNo,
  findAnswerQThreeById,
  findAnswerQThreeByValue,
  checkValidation,
  getOrganiserDateValidation,
  getOrganiserDateValidationStates,
  getStrokeColor,
  ifAddressData,
  inputCharacterCount,
  isFormDataCompleteOrganiser,
  regexValidaton,
  isAvailableNationalityField,
  buildFocusedFieldMap,
  getErrorDetailsQuote,
  getErrorDetailsRenewals,
  validateIdentificationNumber,
  hidePersonalDetailsFields,
} from "@/utils/organiser.utils";
import { travelFromCountryData } from "@/utils/question-location-from.utils";
import {
  organiserMedscrollToAnswer,
  organiserMedscrollToAnswerWithFooter,
  smoothScrollToRef,
} from "@/utils/scrollUtils";
import { isValidDate } from "@/utils/travel-date.utils";
import { useTranslatedTitleData, validateName } from "@/utils/traveller.utils";

import OrganiserDOBField from "./organiser-dob-field";
import Styles from "./organiser-form.module.css";
import IdentificationFields from "./organiser-identification-fields";
import NationalityField from "./organiser-nationality-field";
import TravellerMedicalAnswer from "../../traveller/traveller-medical-answer/traveller-medical-answer";

const formDataInitialValue = {
  id: 1,
  value1: "",
  value2: "",
  value3: "",
  value4: "",
  nationalityValue: undefined,
  dateValue: undefined,
  isDateCalenter: true,
  travelling: undefined,
  postCode: "",
  firstLineOfAddress: "",
  secondLineOfAddress: "",
  city: "",
  country: "",
  hasMedicalConditions: undefined,
  identificationType: undefined,
  identificationNumber: "",
};

const fontConfig = [
  {
    brand: "staysure",
    fontColorClass: "text-cyan-800",
  },
  {
    brand: "avanti",
    fontColorClass: "text-interactive-secondary-default",
  },
];

const disabledTextFieldsStyle = `[&>div>div]:!border-border-disabled
                [&>div>div>input]:!text-text-disabled [&>div>div>input]:!bg-surface-neutral-option-2 
                [&>div>div>label]:!text-text-disabled [&>div>div>label]:!bg-surface-neutral-option-2`;

function OrganiserForm({
  inputOnePlaceholder,
  inputTwoPlaceholder,
  handleSubmitButton,
  inputOneErrorNumericvalues,
  inputOneErrorSpecialCharacters,
  inputOneErrorMaximumNumber,
  inputTwoErrorNumericvalues,
  inputTwoErrorSpecialCharacters,
  inputTwoErrorMaximumNumber,
  dobMinAge,
  dobMaxAge,
  dobPlaceholder,
  travellerType,
  personalStatusPlaceholder,
  nationalityPlaceholder,
  dobMaxLimit,
  questionTwoText,
  organiserFormSaved,
  brand,
  newMedicalFlowEnabled,
  medicalQuestionText,
  medicalQuestionInfoText,
  medicalServiceDeniedText,
  medicalYesBtnText,
  medicalNoBtnText,
  application = "quote",
  renewalInfoText,
  alertMessageIncompleteSectionSingle,
  alertMessageIncompleteSection,
  alertMessageLinkSectionSingle,
  alertMessageLinkSection,
  showErrors,
  firstInputClick,
  ...props
}: Readonly<OrganiserFormProps>) {
  const question = useSelector((state: RootState) => state.quote);
  const { setSelectedState, setHasMedicalConditionState } = useQuestionsState();
  const { t } = useWizardTranslation();
  const [additionalData, setAdditionalData] = useState<boolean>(false);
  const [formData, setFormData] =
    useState<FormDataTypeOrganiser>(formDataInitialValue);
  const [firstNameErrors, setFirstNameErrors] = useState<string>("");
  const [lastNameErrors, setLastNameErrors] = useState<string>("");
  const [addressErrors, setAddressErrors] = useState<string>("");

  const [valueAddress, setValueAddress] = useState<string | null>("");
  const [suggestions, setSuggestions] = useState<AddressType[]>([]);
  const [showSuggestionList, setShowSuggestionList] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce(valueAddress, 500);
  const [addressServiceCountry, setAddressServiceCountry] =
    useState<string>("");
  const { current, wizard } = useWizard();

  const titleData = useTranslatedTitleData(
    current?.brandSpecificParams?.excludedTitles as TitleKey[]
  );

  const getAgeFromPolicyStartForAMT =
    current?.uiElementControls?.getAgeFromPolicyStartForAMT;

  let fromCountry =
    question.attributes.organiser?.country ??
    question.attributes.tripDetails?.fromLocation ??
    current?.brandSpecificParams?.fromCountryCode;

  const isAMT =
    question.attributes.tripDetails?.policyType === ANNUAL_MULTI_TRIP;

  const [address, fetchAddress]: [AddressType[], () => void] = useAddressApi(
    debouncedSearchTerm,
    addressServiceCountry
  );

  const hasMounted = useRef(false);

  const searchAddressByPostCodeEnabled = useMemo(() => {
    return current?.brandSpecificParams?.searchAddressByPostCode;
  }, [current]);

  const defaultFromCountry = useMemo(() => {
    return typeof current?.brandSpecificParams?.defaultFromCountry === "string"
      ? current.brandSpecificParams.defaultFromCountry
      : "GB";
  }, [current]);

  useEffect(() => {
    if (fromCountry && fromCountry !== "") {
      const selectedCountry = travelFromCountryData.find(
        (country) => country.code === fromCountry
      );
      if (selectedCountry) {
        setAddressServiceCountry(selectedCountry?.addressServiceCode);
      } else {
        setAddressServiceCountry("");
      }
    }

    // Set the address service country to the default from country
    if (current?.brandSpecificParams?.fromCountryEnabled) {
      setAddressServiceCountry(defaultFromCountry);
    }
  }, [current, defaultFromCountry, fromCountry]);

  const [addressID, setAddressID] = useState<string>("");
  const [addressDetails, setAddressDetails] = useState<string>("");
  const [addressData, setAddressData] = useState<AddressType[]>([]);
  const [invalideDate, setInvalideDate] = useState<string>("");
  const [activeHoverd, setActiveHoverd] = useState<boolean>(true);
  const [postCode, setPostCode] = useState<string | null>("");
  const [inputDisable, setInputDisable] = useState<boolean>(
    application === "renewal"
  );
  const isStickyFooter = useStickyFooter();

  const [invalidErrorMessage, setInvalidErrorMessage] = React.useState({
    enterAddress: "",
    line1: "",
    line2: "",
    city: "",
    postalCode: "",
  });

  const { getWarningStatus, handleFocus, setAllFocused, setFocusedInputs } =
    useInputFocus({
      value1: false,
      value2: false,
      value3: false,
      value4: false,
      nationalityValue: false,
      dateValue: false,
      postCode: false,
      postalCode: false,
      line1: false,
      line2: false,
      city: false,
      travelling: false,
      hasMedicalConditions: false,
      identificationType: false,
      identificationNumber: false,
    });
  const [isTitleFocused, setIsTitleFocused] = useState<boolean>(false);
  const [isNationalityFocused, setIsNationalityFocused] =
    useState<boolean>(false);
  const [isTabPressed, setIsTabPressed] = useState(false);
  const [addressDataFetch, fetchAddressDetails] =
    useFetchAddressData(addressID);

  const isdesktop = useMediaQuery("(min-width: 600px)");
  const { usagePage } = props;

  const isShowNationalityDropDown = useMemo(() => {
    return !!(
      isAvailableNationalityField ||
      current?.uiElementControls?.showNationalityDropDown
    );
  }, [current]);

  // Fetch nationality list data
  const nationalityDataList = useFetchNationalityData(
    isShowNationalityDropDown
  );

  const nationalityData = useMemo(() => {
    return reorderNationalities(
      nationalityDataList ?? [],
      props.nationalityPopularCountryList ?? []
    );
  }, [nationalityDataList, props.nationalityPopularCountryList]);

  const nationalityId = question?.attributes.organiser?.nationalityId;

  const validateFirstName = (form: FormDataTypeOrganiser) => {
    const errors: string[] = validateName(
      form.value2,
      inputOneErrorNumericvalues ?? "",
      inputOneErrorSpecialCharacters ?? "",
      inputOneErrorMaximumNumber ?? "",
      wizard
    );

    setFirstNameErrors(errors.join(". "));
  };

  const validateLastName = (form: FormDataTypeOrganiser) => {
    const errors: string[] = validateName(
      form.value3,
      inputTwoErrorNumericvalues ?? "",
      inputTwoErrorSpecialCharacters ?? "",
      inputTwoErrorMaximumNumber ?? "",
      wizard
    );

    setLastNameErrors(errors.join(". "));
  };

  const getFocusedFields = useCallback(
    (field: string) => buildFocusedFieldMap(application)[field] ?? [],
    [application]
  );

  const handleInputChange = (
    fieldName: string,
    value: string | boolean | undefined,
    addressArray?: AddressType[]
  ) => {
    let newFormData = formData;

    if (fieldName === "selectAddress") {
      if (ifAddressData(addressArray as AddressType[], current)) {
        const addressObj = addressArray?.[0];
        newFormData.firstLineOfAddress = addressObj?.attributes.line1 ?? "";
        newFormData.secondLineOfAddress = addressObj?.attributes?.line2;
        newFormData.city = addressObj?.attributes?.city ?? "";
        newFormData.postCode =
          addressObj?.attributes?.postalCode?.replace("-", " ") ?? "";
        newFormData.country =
          addressObj?.attributes?.countryIso2 ?? String(fromCountry);
      } else {
        newFormData.firstLineOfAddress = "";
        newFormData.secondLineOfAddress = "";
        newFormData.city = "";
        newFormData.postCode = "";
        newFormData.country = "";
      }
    } else {
      newFormData = { ...formData, [fieldName]: value };
    }

    if (typeof value === "string") {
      // Remove the trailing space if present
      value = value.replace(/\s+$/, "");
    }
    if (fieldName === "line1") {
      newFormData.firstLineOfAddress = value as string;
    }
    if (fieldName === "line2") {
      newFormData.secondLineOfAddress = value as string;
    }
    if (fieldName === "city") {
      newFormData.city = value as string;
    }
    if (fieldName === "postalCode") {
      newFormData.postCode = value as string;
    }

    if (fieldName === "dateValue" || newFormData.dateValue) {
      if (isValidDate(newFormData.dateValue as Date)) {
        const birthDate = dayjs(newFormData.dateValue).startOf("day");
        const currentDate = dayjs().startOf("day");
        const standardAge = currentDate.diff(birthDate, "year");
        const policyStartDate = question.attributes.tripDetails?.startDate
          ? dayjs(question.attributes.tripDetails.startDate).startOf("day")
          : null;
        const AMTAge = policyStartDate?.diff(birthDate, "year");

        const age =
          getAgeFromPolicyStartForAMT && isAMT
            ? AMTAge ?? standardAge
            : standardAge;

        // Check if the birth date is in the future
        const isFutureDate = birthDate.isAfter(currentDate);
        let ageValue = 0;
        if (isFutureDate) {
          if (age === 0) {
            ageValue = -0.5;
          } else if (age < 0) {
            ageValue = age;
          }
        } else {
          ageValue = age;
        }
        newFormData = {
          ...newFormData,
          value4: ageValue.toString(),
        };
      } else {
        newFormData = {
          ...newFormData,
          value4: "",
        };
      }
    }
    if (fieldName === "value2") {
      validateFirstName(newFormData);
    } else if (fieldName === "value3") {
      validateLastName(newFormData);
    }

    newFormData.country = String(fromCountry ?? "GB");

    newFormData = {
      ...newFormData,
      ...{ [fieldName]: value },
    };

    if (fieldName === "nationalityValue") {
      const selectedNationality = nationalityData.find((n) => n.name === value)
        ?.id;
      newFormData.nationalityValue = selectedNationality ?? undefined;
      if (selectedNationality) {
        setFocusedInputs(getFocusedFields("nationalityValue"));
      }
    }
    setFormData(newFormData);

    const isComplete = isFormDataCompleteOrganiser(
      newFormData,
      travellerType ?? Individual,
      wizard,
      current,
      question.attributes?.experimentDetails
    );
    if (isComplete) {
      handleSubmitButton(newFormData);
    } else {
      handleSubmitButton({} as FormDataTypeOrganiser);
    }
  };

  const handleChange = (event: string, inputName: string) => {
    const isError = checkValidation(
      inputName,
      addressServiceCountry,
      props
    ).map((validation) => {
      if (validation.errorType === "regex") {
        return regexValidaton(event, validation.validation as RegExp)
          ? validation.errorMsg
          : null;
      }
      if (validation.errorType === "maxTextCount") {
        return inputCharacterCount(event, validation.validation as number)
          ? validation.errorMsg
          : null;
      }
      return null;
    });

    handleInputChange(inputName, event);

    if (isError.length > 0) {
      setInvalidErrorMessage((prev) => {
        return {
          ...prev,
          [inputName]: event ? isError[0] ?? isError[1] : null,
        };
      });
    }

    setAddressData((prev) => {
      return [
        {
          ...prev[0],
          attributes: {
            ...prev[0]?.attributes,
            [inputName]: event,
          },
        },
      ];
    });
  };

  const handleChange1 = (event: string) => {
    const trimSearchValue = event.trim();

    if (!trimSearchValue) {
      setAddressErrors("");
      handleInputChange("selectAddress", undefined, []);
      setShowSuggestionList(false);
    } else if (trimSearchValue && trimSearchValue.length < 3) {
      setSuggestions([]);
      setShowSuggestionList(false);
    }
    if (trimSearchValue && trimSearchValue.length > 2) {
      if (trimSearchValue.length === 3) setSuggestions([]); // clear existing data
      if (trimSearchValue === valueAddress) {
        setSuggestions(address);
      }
      setValueAddress(trimSearchValue);
      setShowSuggestionList(true);
    } else {
      setValueAddress("");
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (addressID && addressID === suggestion.id) {
      if (addressDataFetch?.[0]) {
        setAddressData(addressDataFetch);

        if (addressDataFetch[0]) {
          const addressObj = addressDataFetch[0] as AddressType;
          const addressModifined: AddressObj = {
            line1: addressObj?.attributes.line1 || "",
            line2: addressObj?.attributes?.line2,
            city: addressObj?.attributes?.city || "",
            postalCode:
              addressObj?.attributes?.postalCode
                ?.replace("-", " ")
                ?.toUpperCase() || "",
            // countryIso2: addressObj?.attributes?.countryIso2 || "",
            countryIso2:
              addressObj?.attributes?.countryIso2 ?? String(fromCountry),
          };
          setPostCode(
            addressObj?.attributes?.postalCode?.replace("-", " ")?.toUpperCase()
          );

          let attrName: keyof AddressObj;

          for (attrName in addressModifined) {
            if (
              Object.prototype.hasOwnProperty.call(addressModifined, attrName)
            ) {
              const propertyValue = addressModifined[attrName];
              if (
                (attrName === "line2" && propertyValue) ||
                attrName !== "line2"
              ) {
                handleChange(propertyValue, attrName);
              }
            }
          }
        }
        setTimeout(
          () => handleInputChange("selectAddress", undefined, addressDataFetch),
          0
        );
      }
    }

    setAddressID(suggestion.id);
    setAddressDetails(
      `${suggestion.attributes.address}, ${suggestion.attributes.description}`
    );

    setShowSuggestionList(false);
  };

  const handleIsPersonTravelling = (answer: number) => {
    if (answer) {
      const isTraveling = findAnswerQThreeById(answer, t)?.value;

      // de select the previous selected answer
      if (formData.travelling === isTraveling) {
        setSelectedState(undefined);
        handleInputChange("travelling", undefined);
        return;
      }

      setSelectedState(isTraveling as boolean);
      handleInputChange("travelling", isTraveling || false);
    }
  };

  const handleMedicalQuestion = (answer: number) => {
    if (answer && newMedicalFlowEnabled) {
      const isMedicalOk = findAnswerQThreeById(answer, t)?.value;
      if (formData.hasMedicalConditions === isMedicalOk) {
        // remove the previous selected answer
        setHasMedicalConditionState(undefined);
        handleInputChange("hasMedicalConditions", undefined);
        return;
      }
      setHasMedicalConditionState(isMedicalOk || false);
      handleInputChange("hasMedicalConditions", isMedicalOk || false);
      setFocusedInputs(getFocusedFields("hasMedicalConditions"));
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const calculateAge = () => {
    const value = Number(formData.value4);
    return value <= 0 ? null : value;
  };

  function getTextColorClasses(lBrand: string | undefined): string {
    let fontClasses = "";
    fontConfig.forEach((config) => {
      if (config.brand === lBrand) {
        fontClasses = `${config.fontColorClass}`;
      }
    });
    return fontClasses;
  }

  useEffect(() => {
    if (organiserFormSaved) {
      setFormData(organiserFormSaved);
      const addressSaved = {
        attributes: {
          line1: organiserFormSaved.firstLineOfAddress,
          line2: organiserFormSaved.secondLineOfAddress,
          city: organiserFormSaved?.city,
          postalCode:
            organiserFormSaved?.postCode?.replace("-", " ")?.toUpperCase() ??
            "",
          // countryIso2: organiserFormSaved?.country,
          countryIso2: organiserFormSaved.country ?? String(fromCountry),
        },
      };
      setPostCode(
        organiserFormSaved?.postCode?.replace("-", " ")?.toUpperCase() ?? ""
      );
      setAdditionalData(true);

      setAddressData([addressSaved] as any);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organiserFormSaved]);

  useEffect(() => {
    if (valueAddress) {
      fetchAddress();
    } else {
      setSuggestions([]);
      setShowSuggestionList(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setSuggestions(address);
    if (address && address.length > 0) {
      setAddressErrors("");
    } else if (valueAddress && valueAddress.length > 0) {
      setAddressErrors(
        props?.adressInputErrorMasg ||
          t("address_invalid_postcode", "Please enter a valid postcode") ||
          props?.postcodeErrorMasg ||
          ""
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    if (!valueAddress) {
      setAddressErrors("");
    }
  }, [valueAddress]);

  useEffect(() => {
    if (suggestions && suggestions.length > 0) {
      setAddressErrors("");
    }
  }, [suggestions]);

  useEffect(() => {
    if (addressDataFetch?.[0]) {
      setAddressData(addressDataFetch);

      if (addressDataFetch[0]) {
        const addressObj = addressDataFetch[0] as AddressType;
        const addressModifined: AddressObj = {
          line1: addressObj?.attributes.line1 || "",
          line2: addressObj?.attributes?.line2,
          city: addressObj?.attributes?.city || "",
          postalCode:
            addressObj?.attributes?.postalCode.replace("-", " ") || "",
          // countryIso2: addressObj?.attributes?.countryIso2 || "",
          countryIso2:
            addressObj?.attributes?.countryIso2 ?? String(fromCountry),
        };

        setPostCode(addressObj?.attributes?.postalCode.replace("-", " "));

        let attrName: keyof AddressObj;

        for (attrName in addressModifined) {
          if (
            Object.prototype.hasOwnProperty.call(addressModifined, attrName)
          ) {
            const propertyValue = addressModifined[attrName];
            if (
              (attrName === "line2" && propertyValue) ||
              attrName !== "line2"
            ) {
              handleChange(propertyValue, attrName);
            }
          }
        }
      }
      handleInputChange("selectAddress", undefined, addressDataFetch);
    } else {
      setAddressData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressDataFetch]);

  useEffect(() => {
    if (addressID) {
      fetchAddressDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressID]);

  useEffect(() => {
    function handleKeyDown(event: { key: string }) {
      if (event.key === "Tab") {
        setIsTabPressed(true);
      }
    }

    function handleKeyUp(event: { key: string }) {
      if (event.key === "Tab") {
        setIsTabPressed(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Click-outside handler for address suggestion list
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionContainerRef.current &&
        !suggestionContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestionList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (newMedicalFlowEnabled) {
      if (formData.travelling === undefined || formData.travelling === false) {
        handleInputChange("hasMedicalConditions", undefined);
        setHasMedicalConditionState(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.travelling]);

  useEffect(() => {
    if (showErrors) setAllFocused();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrors]);

  const filterUnsupportedTitlesForSpainQuote =
    current?.brandSpecificParams?.removeUnsupportedTitles;
  const searchParams = useSearchParams();
  const editPageTraveller = searchParams.get(TravellerEdit);
  const editPageOrganizer = searchParams.get(OrganizerEdit);

  const isEditFlow =
    editPageTraveller === "true" || editPageOrganizer === "true";

  const isUnsupportedTitle = useMemo(() => {
    return filterUnsupportedTitlesForSpainQuote && isEditFlow;
  }, [filterUnsupportedTitlesForSpainQuote, isEditFlow]);

  useEffect(() => {
    const value = formData.value1;

    if (isUnsupportedTitle && (value === "Dr" || value === "Rev")) {
      handleInputChange("value1", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.value1, isUnsupportedTitle]);

  const titleStatus = useMemo(() => {
    return formData.value1 ? "success" : getWarningStatus("value1");
  }, [formData.value1, getWarningStatus]);

  const firstNameStatus = useMemo(() => {
    if (firstNameErrors) return "error";
    if (formData.value2) return "success";
    return getWarningStatus("value2");
  }, [firstNameErrors, formData.value2, getWarningStatus]);

  const lastNameStatus = useMemo(() => {
    if (lastNameErrors) return "error";
    if (formData.value3) return "success";
    return getWarningStatus("value3");
  }, [formData.value3, getWarningStatus, lastNameErrors]);

  const nationalityStatus = useMemo(() => {
    return formData.nationalityValue
      ? "success"
      : getWarningStatus("nationalityValue");
  }, [formData.nationalityValue, getWarningStatus]);

  const showIdentificationFields = useMemo(() => {
    return current?.brandSpecificParams?.showIdentificationFields === true;
  }, [current]);

  // Check if user is in Spain  A/B test variant (hide address and ID fields)
  const isPersonalDetailsDisabled = useMemo(() => {
    return hidePersonalDetailsFields(question.attributes?.experimentDetails);
  }, [question.attributes?.experimentDetails]);

  const dobStatus = useMemo(() => {
    if (getOrganiserDateValidationStates(formData, current) || invalideDate)
      return "error";
    if (formData.dateValue && isValidDate(formData.dateValue)) return "success";
    return getWarningStatus("dateValue");
  }, [formData, invalideDate, getWarningStatus, current]);

  const identificationTypeStatus = useMemo(() => {
    if (!showIdentificationFields) return null;
    return formData.identificationType
      ? "success"
      : getWarningStatus("identificationType");
  }, [formData.identificationType, getWarningStatus, showIdentificationFields]);

  const identificationNumberStatus = useMemo(() => {
    if (!showIdentificationFields) return null;

    // Special case: user typing without selecting type
    if (!formData.identificationType && formData.identificationNumber) {
      return "error";
    }

    // Don't show success/error until type is selected
    if (!formData.identificationType) {
      return getWarningStatus("identificationNumber");
    }

    // If there's a value, validate it
    if (formData.identificationNumber?.trim()) {
      const error = validateIdentificationNumber(
        formData.identificationType,
        formData.identificationNumber,
        props.identificationNumberInvalidError || "Invalid format"
      );

      if (error) return "error";
      return "success";
    }

    // Empty field after interaction
    return getWarningStatus("identificationNumber");
  }, [
    formData.identificationType,
    formData.identificationNumber,
    getWarningStatus,
    showIdentificationFields,
    props.identificationNumberInvalidError,
  ]);

  const firstNameRef = useRef<HTMLButtonElement | null>(null);
  const lastNameRef = useRef<HTMLButtonElement | null>(null);
  const dobRef = useRef<HTMLButtonElement | null>(null);
  const postCodeRef = useRef<HTMLButtonElement | null>(null);
  const addressLine1Ref = useRef<HTMLButtonElement | null>(null);
  const addressLine2Ref = useRef<HTMLButtonElement | null>(null);
  const cityRef = useRef<HTMLButtonElement | null>(null);
  const manualPostCodeRef = useRef<HTMLButtonElement | null>(null);
  const questionTwoRef = useRef<HTMLDivElement | null>(null);
  const medicalAnswerRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const nationalityRef = useRef<HTMLDivElement | null>(null);
  const identificationTypeRef = useRef<HTMLDivElement | null>(null);
  const identificationNumberRef = useRef<HTMLButtonElement | null>(null);
  const suggestionContainerRef = useRef<HTMLDivElement | null>(null);

  const postCodeStatus = useMemo(() => {
    if (!additionalData && !addressDetails) {
      if (addressErrors) return "error";
      if (formData.postCode) return "success";
      return getWarningStatus("postCode");
    }
    return null;
  }, [
    additionalData,
    addressDetails,
    addressErrors,
    formData.postCode,
    getWarningStatus,
  ]);

  const addressLine1Status = useMemo(() => {
    if (invalidErrorMessage.line1) return "error";
    if (addressData[0]?.attributes?.line1) return "success";
    return getWarningStatus("line1");
  }, [addressData, getWarningStatus, invalidErrorMessage.line1]);

  const addressLine2Status = useMemo(() => {
    if (invalidErrorMessage?.line2) return "error";
    if (addressData[0]?.attributes?.line2) return "success";
    return null;
  }, [addressData, invalidErrorMessage?.line2]);

  const addressCityStatus = useMemo(() => {
    if (invalidErrorMessage.city) return "error";
    if (addressData[0]?.attributes?.city) return "success";
    return getWarningStatus("city");
  }, [addressData, getWarningStatus, invalidErrorMessage.city]);

  const addressPostalCodeStatus = useMemo(() => {
    if (
      current?.brandSpecificParams?.postCodeOptional &&
      !addressData[0]?.attributes?.postalCode?.length
    ) {
      return null;
    }
    if (
      addressData[0]?.attributes?.postalCode?.length > 2 &&
      invalidErrorMessage.postalCode
    ) {
      return "error";
    }

    if (formData.postCode && formData.postCode.length > 2) return "success"; // no status until 3 chars

    return getWarningStatus("postalCode");
  }, [
    current?.brandSpecificParams?.postCodeOptional,
    addressData,
    formData.postCode,
    getWarningStatus,
    invalidErrorMessage.postalCode,
  ]);

  const errorDetails: { count: number; firstError: string | null } =
    useMemo(() => {
      const shouldPassAdditionalData = searchAddressByPostCodeEnabled
        ? additionalData
        : true;
      const data = {
        showErrors,
        errorStatus: {
          titleStatus,
          firstNameStatus,
          lastNameStatus,
          identificationTypeStatus,
          identificationNumberStatus,
          postCodeStatus,
          addressLine1Status,
          addressLine2Status,
          addressCityStatus,
          addressPostalCodeStatus,
          dobStatus,
          nationalityStatus,
        },
        additionalData: shouldPassAdditionalData,
        addressDetails,
        travelling: formData.travelling,
        hasMedicalConditions: formData.hasMedicalConditions,
        newMedicalFlowEnabled,
        travellerType,
        experimentDetails: question.attributes?.experimentDetails,
      };
      return application === "quote"
        ? getErrorDetailsQuote(data)
        : getErrorDetailsRenewals(data);
    }, [
      searchAddressByPostCodeEnabled,
      additionalData,
      showErrors,
      titleStatus,
      firstNameStatus,
      lastNameStatus,
      identificationTypeStatus,
      identificationNumberStatus,
      postCodeStatus,
      addressLine1Status,
      addressLine2Status,
      addressCityStatus,
      addressPostalCodeStatus,
      dobStatus,
      nationalityStatus,
      addressDetails,
      formData.travelling,
      formData.hasMedicalConditions,
      newMedicalFlowEnabled,
      travellerType,
      application,
      question.attributes?.experimentDetails,
    ]);

  const getAlertMessageIncompleteSection = useCallback(() => {
    if (errorDetails.count === 1) {
      return alertMessageIncompleteSectionSingle.replace(
        "<ERROR_COUNT>",
        errorDetails.count.toString()
      );
    }
    return alertMessageIncompleteSection.replace(
      "<ERROR_COUNT>",
      errorDetails.count.toString()
    );
  }, [
    alertMessageIncompleteSection,
    alertMessageIncompleteSectionSingle,
    errorDetails.count,
  ]);

  const getAlertMessageLinkSection = useCallback(() => {
    if (errorDetails.count === 1) {
      return alertMessageLinkSectionSingle;
    }
    return alertMessageLinkSection;
  }, [
    alertMessageLinkSection,
    alertMessageLinkSectionSingle,
    errorDetails.count,
  ]);

  const firstErrorRef = useRef<HTMLDivElement>(null);

  const clearAutofillPostCodeData = () => {
    setSuggestions([]);
    handleChange1("");
    setPostCode("");
  };

  const handleWebChatClick = () => {
    showMessenger();
  };

  const renderIdentificationFields = () => (
    <IdentificationFields
      identificationType={formData.identificationType}
      identificationNumber={formData.identificationNumber}
      identificationTypeStatus={identificationTypeStatus}
      identificationNumberStatus={identificationNumberStatus}
      identificationTypePlaceholder={
        props.identificationTypePlaceholder ?? "Tipo de identificación"
      }
      identificationNumberPlaceholder={
        props.identificationNumberPlaceholder ?? "Número de identificación"
      }
      identificationTypeRequiredError={
        props.identificationTypeRequiredError ?? ""
      }
      identificationNumberInvalidError={
        props.identificationNumberInvalidError ?? ""
      }
      identificationNumberWarning={props.identificationNumberWarning ?? ""}
      identificationTypeDNILabel={props.identificationTypeDNILabel}
      identificationTypeNIELabel={props.identificationTypeNIELabel}
      identificationTypePassportLabel={props.identificationTypePassportLabel}
      brand={brand}
      handleInputChange={handleInputChange}
      setFocusedInputs={setFocusedInputs}
      getFocusedFields={getFocusedFields}
      isTabPressed={isTabPressed}
      errorDetailsFirstError={errorDetails.firstError}
      firstErrorRef={firstErrorRef}
      identificationTypeRef={identificationTypeRef}
      identificationNumberRef={identificationNumberRef}
      prevFieldRef={cityRef}
      hasMounted={hasMounted}
      Styles={Styles}
    />
  );

  const renderNationality = () => (
    <NationalityField
      nationalityStatus={nationalityStatus}
      errorDetailsFirstError={errorDetails.firstError}
      firstErrorRef={firstErrorRef}
      nationalityRef={nationalityRef}
      isNationalityFocused={isNationalityFocused}
      setIsNationalityFocused={setIsNationalityFocused}
      isTabPressed={isTabPressed}
      nationalityData={nationalityData}
      nationalityId={nationalityId}
      nationalityPlaceholder={nationalityPlaceholder}
      brand={brand}
      handleInputChange={handleInputChange}
      setFocusedInputs={setFocusedInputs}
      getFocusedFields={getFocusedFields}
      hasMounted={hasMounted}
      isdesktop={isdesktop}
      setActiveHoverd={setActiveHoverd}
      setInputDisable={setInputDisable}
      Styles={Styles}
      firstNameRef={dobRef}
      childRef={
        errorDetails.firstError === "nationalityValue"
          ? firstErrorRef
          : undefined
      }
    />
  );

  const renderDOB = () => (
    <OrganiserDOBField
      application={application}
      dobStatus={dobStatus}
      errorDetails={errorDetails}
      formData={formData}
      dobPlaceholder={dobPlaceholder}
      brand={brand}
      dobMaxLimit={dobMaxLimit}
      dobMinAge={dobMinAge}
      dobMaxAge={dobMaxAge}
      travellerType={travellerType}
      invalideDate={invalideDate}
      calculateAge={calculateAge}
      handleInputChange={handleInputChange}
      setInvalideDate={setInvalideDate}
      setFocusedInputs={setFocusedInputs}
      getFocusedFields={getFocusedFields}
      getOrganiserDateValidation={getOrganiserDateValidation}
      Styles={Styles}
      dobRef={dobRef}
      firstErrorRef={firstErrorRef}
      lastNameRef={cityRef}
      firstNameRef={lastNameRef}
      isAvailableNationalityField={!!(additionalData || addressDetails)}
      childRef={
        errorDetails.firstError === "dateValue" ? firstErrorRef : undefined
      }
      dateValue={formData.dateValue}
      usagePage={usagePage}
    />
  );

  const selectedPostCode = useMemo(() => {
    if (postCode && postCode !== "") return postCode;
    return addressData[0]?.attributes?.postalCode
      ? addressData[0].attributes.postalCode
      : "";
  }, [addressData, postCode]);

  const handleSearchByPostCode = () => {
    setAdditionalData(false);
    setAddressDetails("");
    // reset manual postcode data
    handleChange("", "line1");
    handleChange("", "line2");
    handleChange("", "city");
    handleChange("", "postalCode");
    // reset autofill postcode data
    clearAutofillPostCodeData();
    // mark as touched
    setFocusedInputs(getFocusedFields("postalCode"));
  };

  return (
    <div className=" flex flex-col">
      <div className=" flex flex-col gap-x-2 md:mt-spacing-l mt-spacing-m">
        <form
          data-testid="organiser-form"
          onSubmit={handleSubmit}
          className={`${Styles.trvellers_form} md:bg-surface-neutral-option-1 bg-surface-neutral-option-2`}
        >
          <div
            key={formData.id}
            className=" field-block md:!pb-spacing-s !px-0"
          >
            <InputValidationWrapper
              className="flex flex-row mb-spacing-s"
              status={titleStatus}
              childRef={
                errorDetails.firstError === "value1" ? firstErrorRef : undefined
              }
            >
              <div
                className={` ${Styles.country_wrapper} ${
                  Styles.title_dropdown
                } ${
                  isTitleFocused && Styles.dropdown_focused
                } flex flex-row items-start  max-w-[106px] w-full h-[56px]`}
                onFocus={() => {
                  if (isTabPressed) {
                    setIsTitleFocused(true);
                  }
                }}
                onBlur={() => setIsTitleFocused(false)}
                id="main-header-id"
                ref={titleRef}
              >
                <Dropdown
                  defaultValue={formData.value1}
                  placeholderText={personalStatusPlaceholder}
                  strokeColor={getStrokeColor(brand)}
                  placeholderTextStyle={{
                    color: "#4f4f4f",
                  }}
                  openDelayMs={200}
                  enableOpenDelay
                  onSelectionChange={(e: any) => {
                    handleInputChange("value1", e);
                    if (application === "quote") {
                      if (isdesktop) {
                        setActiveHoverd(false);
                        setInputDisable(true);
                        setTimeout(() => {
                          setInputDisable(false);
                        }, 1);
                        setTimeout(() => {
                          setActiveHoverd(true);
                        }, 1200);
                      } else {
                        setActiveHoverd(false);
                        setInputDisable(true);
                        setTimeout(() => {
                          setInputDisable(false);
                        }, 1);
                      }
                    }
                  }}
                  onDropdownBlur={() => {
                    if (getWarningStatus("value1") === null) {
                      handleFocus("value1");
                    }
                  }}
                  className={`${Styles.droplistBox} ${
                    titleStatus === "warning" ? Styles.warningMsgClass : ""
                  } ${
                    titleStatus === "success" ? Styles.successMsgClass : ""
                  } border-interactive-secondary-default rounded-radius-lg
                  max-h-spacing-xxxl`}
                  listBoxStyle={{
                    outline: "none",
                    margin: "0px 0px 0px 0px",
                    minWidth: "unset !important",
                    boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
                  }}
                  listClassName={` ${Styles.listStyle} mt-[10px] rounded-radius-s`}
                  unorderedListClassName="w-[100px] !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg
               !px-spacing-m !py-spacing-xs"
                >
                  {titleData?.map((title) => (
                    <DropdownItem value={title.displayName} key={title.id}>
                      <span className="w-full leading-[24px] font-proximanova text-2xs  !text-text-functional font-semibold p-spacing-xs country-name">
                        {title.displayName}
                      </span>
                    </DropdownItem>
                  ))}
                </Dropdown>
              </div>
            </InputValidationWrapper>
            <button
              type="button"
              className="w-full focus-visible:outline-none "
              tabIndex={-1}
              ref={firstNameRef}
              onFocus={() => {
                firstInputClick?.();
              }}
              data-testid="fist-name-scroll-id"
            >
              <InputValidationWrapper
                className={`${
                  Styles.input_wrapper
                } flex flex-row items-start mb-spacing-s ${
                  application === "renewal" ? disabledTextFieldsStyle : ""
                }`}
                status={firstNameStatus}
                childRef={
                  errorDetails.firstError === "value2"
                    ? firstErrorRef
                    : undefined
                }
              >
                <TextFieldSts
                  isDisabled={inputDisable}
                  activeHoverd={activeHoverd}
                  label={inputOnePlaceholder ?? ""}
                  data-testid="first-name-organiser"
                  id="first-name-organiser"
                  aria-label={t("aria_first_name", "first-name-organiser")}
                  width="100%"
                  name="value2"
                  maxLength={51}
                  state={firstNameStatus ?? undefined}
                  message={firstNameErrors}
                  value={formData.value2}
                  onChange={(value) => handleInputChange("value2", value)}
                  onBlur={() => setFocusedInputs(getFocusedFields("value2"))}
                  msgCustomStyles={{ width: "auto" }}
                />
              </InputValidationWrapper>
            </button>
            <button
              className="w-full focus-visible:outline-none "
              tabIndex={-1}
              type="button"
              ref={lastNameRef}
              onFocus={() => {
                smoothScrollToRef(titleRef, 100);
              }}
              data-testid="last-name-scroll-id"
            >
              <InputValidationWrapper
                className={`${
                  Styles.input_wrapper
                } flex flex-row items-start mb-spacing-s ${
                  application === "renewal" ? disabledTextFieldsStyle : ""
                }`}
                status={lastNameStatus}
                childRef={
                  errorDetails.firstError === "value3"
                    ? firstErrorRef
                    : undefined
                }
              >
                <TextFieldSts
                  isDisabled={inputDisable}
                  activeHoverd={activeHoverd}
                  label={inputTwoPlaceholder ?? ""}
                  data-testid="last-name-organiser"
                  id="last-name-organiser"
                  aria-label={t("aria_last_name", "last-name-organiser")}
                  width="100%"
                  name="value3"
                  maxLength={51}
                  state={lastNameStatus ?? undefined}
                  message={lastNameErrors}
                  value={formData.value3}
                  onChange={(value) => handleInputChange("value3", value)}
                  onBlur={() => setFocusedInputs(getFocusedFields("value3"))}
                  msgCustomStyles={{ width: "auto" }}
                />
              </InputValidationWrapper>
            </button>

            {/* NATIONALITY */}
            {application === "renewal" &&
              isShowNationalityDropDown &&
              renderNationality()}

            {application === "renewal" && renderDOB()}

            {renewalInfoText && (
              <div className="mt-spacing-s  w-full">
                <InlineInfoBox
                  tabIndex={-1}
                  className={`px-spacing-m py-spacing-xs w-full font-body-font font-normal
                          text-2xs text-text-functional rounded-lg border !border-border-select
                          mt-spacing-s mb-0 mx-auto leading-spacing-l bg-surface-info-2 ${Styles.infoBox}`}
                  content={
                    <RichText
                      richText={renewalInfoText}
                      onClick={handleWebChatClick}
                    />
                  }
                />
              </div>
            )}

            {/* Hide address block for Spain  A/B test variant */}
            {!isPersonalDetailsDisabled && (
              <div className={` ${Styles.address_block}`}>
                {searchAddressByPostCodeEnabled && (
                  <div
                    className="md:text-xs text-left text-2xs relative"
                    ref={suggestionContainerRef}
                  >
                    {!additionalData && !addressDetails && (
                      <InputValidationWrapper
                        className={`postcode-wrapper font-body-font flex flex-row ${
                          application === "renewal" ? "mt-spacing-s" : ""
                        }`}
                        status={postCodeStatus}
                        childRef={
                          errorDetails.firstError === "postCode"
                            ? firstErrorRef
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          ref={postCodeRef}
                          className="w-full focus-visible:outline-none "
                          data-testid="postcode-scroll-id"
                          tabIndex={-1}
                          onFocus={() => {
                            smoothScrollToRef(firstNameRef, 100);
                          }}
                          onClick={() => {
                            if (suggestions.length > 0) {
                              setShowSuggestionList(true);
                            }
                          }}
                        >
                          <TextFieldSts
                            activeHoverd={activeHoverd}
                            id="scrollableInput"
                            type="text"
                            name="address"
                            data-testid="address-organiser"
                            width="100%"
                            autoComplete="off"
                            state={postCodeStatus ?? undefined}
                            message={addressErrors || ""}
                            placeholder={props?.adressInputPlaceholder}
                            value={postCode ?? ""}
                            onChange={(e) => {
                              handleChange1(e);
                            }}
                            label="address"
                            labelLeftPadding="16px"
                            onFocus={() => {
                              if (suggestions.length > 0) {
                                setShowSuggestionList(true);
                              }
                            }}
                            onBlur={() =>
                              setFocusedInputs(getFocusedFields("postCode"))
                            }
                            msgCustomStyles={{ width: "auto" }}
                          />
                        </button>
                      </InputValidationWrapper>
                    )}
                    {suggestions?.[0] && showSuggestionList && (
                      <ul
                        className={`${
                          Styles.scroll
                        } absolute z-10 bg-white w-full border-gray-300 rounded-lg mt-1 ${
                          suggestions.length > 5
                            ? "overflow-y-scroll md:max-h-[215px] sm:!max-h-[170px]"
                            : ""
                        }`}
                      >
                        {suggestions.map((suggestion) => (
                          <li
                            key={suggestion.id}
                            className="cursor-pointer p-2 hover:bg-gray-100"
                          >
                            <button
                              id={`suggestion-button-${suggestion.id}`}
                              type="button"
                              className="w-full h-full text-left "
                              onClick={() => {
                                const element1 =
                                  document.getElementById("paragaph-test-id");
                                if (element1) {
                                  const actionBtnElement =
                                    document.getElementById(
                                      "organiser-page-submit-button"
                                    );
                                  actionBtnElement?.scrollIntoView({
                                    behavior: "smooth",
                                    block: !isStickyFooter
                                      ? "nearest"
                                      : "start",
                                  });
                                } else {
                                  smoothScrollToRef(
                                    isShowNationalityDropDown
                                      ? nationalityRef
                                      : questionTwoRef,
                                    500
                                  );
                                }
                                handleSuggestionClick(suggestion);
                                setFocusedInputs(getFocusedFields("postCode"));
                                clearAutofillPostCodeData();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSuggestionClick(suggestion);
                                  setFocusedInputs(
                                    getFocusedFields("postCode")
                                  );
                                  clearAutofillPostCodeData();
                                }
                              }}
                            >
                              {`${suggestion.attributes.address}, ${suggestion.attributes.description}`}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {!additionalData && !addressDetails && (
                      <div className=" flex mt-spacing-xs mb-spacing-s">
                        <Button
                          id="enterManuallyButton"
                          btnType="label"
                          type="button"
                          label={props?.enterAddressButton ?? ""}
                          className="text-interactive-secondary-default text-2xs font-semibold font-['Proxima Nova'] underline leading-normal"
                          onClick={() => {
                            setAdditionalData(true);
                            clearAutofillPostCodeData();
                          }}
                          isOnlyActiveOnPress
                        />
                      </div>
                    )}
                  </div>
                )}

                {(!searchAddressByPostCodeEnabled ||
                  additionalData ||
                  addressDetails) && (
                  <div
                    className={`md:text-xs text-left text-2xs ${
                      application === "renewal"
                        ? "mb-spacing-l"
                        : "mb-spacing-s"
                    }`}
                  >
                    <TransformAnimation
                      currentPage={usagePage as string}
                      isSubSectionFilled={
                        !!addressData[0]?.attributes?.postalCode
                      }
                    >
                      <button
                        type="button"
                        data-testid="addres-line1-datetest-id"
                        ref={addressLine1Ref}
                        className="w-full focus-visible:outline-none"
                        tabIndex={-1}
                        onFocus={() => {
                          smoothScrollToRef(firstNameRef, 100);
                        }}
                      >
                        <InputValidationWrapper
                          className={`flex ${Styles.address_field} ${
                            application === "renewal" ? "mt-spacing-s" : ""
                          }`}
                          status={addressLine1Status}
                          childRef={
                            errorDetails.firstError === "line1"
                              ? firstErrorRef
                              : undefined
                          }
                        >
                          <TextFieldSts
                            activeHoverd={activeHoverd}
                            label={props?.addressLineOne}
                            name="line1"
                            id="addressLineOne"
                            width="100%"
                            state={addressLine1Status ?? undefined}
                            message={invalidErrorMessage.line1 || ""}
                            onChange={(e) => {
                              handleChange(e, "line1");
                            }}
                            onBlur={() => {
                              setFocusedInputs(getFocusedFields("line1"));
                            }}
                            value={addressData[0]?.attributes?.line1 ?? ""}
                            maxLength={MAX_ADDRESS_LENGTH}
                            labelLeftPadding="16px"
                            msgCustomStyles={{ width: "auto" }}
                            data-testid="addressLineOne"
                            brand={
                              application === "renewal" ? brand : undefined
                            }
                          />
                        </InputValidationWrapper>
                      </button>
                      <button
                        type="button"
                        className="w-full focus-visible:outline-none"
                        tabIndex={-1}
                        ref={addressLine2Ref}
                        onFocus={() => {
                          smoothScrollToRef(lastNameRef, 100);
                        }}
                        data-testid="address-line2-datetest-id"
                      >
                        <InputValidationWrapper
                          className={`flex ${Styles.address_field} mt-spacing-s`}
                          status={addressLine2Status}
                          childRef={
                            errorDetails.firstError === "line2"
                              ? firstErrorRef
                              : undefined
                          }
                        >
                          <TextFieldSts
                            activeHoverd={activeHoverd}
                            label={props?.addressLineTwo}
                            name="line2"
                            id="addressLineTwo"
                            width="100%"
                            state={addressLine2Status ?? undefined}
                            message={invalidErrorMessage?.line2 || ""}
                            onChange={(e) => {
                              handleChange(e, "line2");
                            }}
                            onBlur={() => {
                              setFocusedInputs(getFocusedFields("line2"));
                            }}
                            value={addressData[0]?.attributes?.line2 ?? ""}
                            maxLength={MAX_ADDRESS_LENGTH}
                            labelLeftPadding="16px"
                            msgCustomStyles={{ width: "auto" }}
                            data-testid="addressLineTwo"
                            brand={
                              application === "renewal" ? brand : undefined
                            }
                          />
                        </InputValidationWrapper>
                      </button>
                      <button
                        type="button"
                        data-testid="city-scroll-id"
                        className="w-full focus-visible:outline-none"
                        tabIndex={-1}
                        ref={cityRef}
                        onFocus={() => {
                          smoothScrollToRef(addressLine1Ref, 100);
                        }}
                      >
                        <InputValidationWrapper
                          className={`flex ${Styles.address_field} mt-spacing-s`}
                          status={addressCityStatus}
                          childRef={
                            errorDetails.firstError === "city"
                              ? firstErrorRef
                              : undefined
                          }
                        >
                          <TextFieldSts
                            activeHoverd={activeHoverd}
                            label={props?.TownCityPlaceholder}
                            name="city"
                            id="selectCity"
                            width="100%"
                            autoComplete="off"
                            state={addressCityStatus ?? undefined}
                            message={invalidErrorMessage.city || ""}
                            onBlur={() => {
                              setFocusedInputs(getFocusedFields("city"));
                            }}
                            onChange={(e) => {
                              handleChange(e, "city");
                            }}
                            value={addressData[0]?.attributes?.city ?? ""}
                            maxLength={40}
                            labelLeftPadding="16px"
                            msgCustomStyles={{ width: "auto" }}
                            brand={
                              application === "renewal" ? brand : undefined
                            }
                          />
                        </InputValidationWrapper>
                      </button>
                      <button
                        type="button"
                        data-testid="manual-postCode-scroll-id"
                        className="w-full focus-visible:outline-none"
                        tabIndex={-1}
                        ref={manualPostCodeRef}
                        onFocus={() => {
                          smoothScrollToRef(addressLine2Ref, 100);
                        }}
                      >
                        <InputValidationWrapper
                          className="flex mt-spacing-s"
                          status={addressPostalCodeStatus}
                          dataTestid="postcode-wrapper"
                          childRef={
                            errorDetails.firstError === "postalCode"
                              ? firstErrorRef
                              : undefined
                          }
                        >
                          <TextFieldSts
                            label={props?.postcodePlaceholder}
                            name="postalCode"
                            id="postcodePlaceholder"
                            width="100%"
                            state={addressPostalCodeStatus ?? undefined}
                            onBlur={() => {
                              setFocusedInputs(getFocusedFields("postalCode"));
                            }}
                            message={
                              (addressData[0]?.attributes?.postalCode?.length >
                                2 &&
                                invalidErrorMessage.postalCode) ||
                              ""
                            }
                            onChange={(e) => {
                              handleChange(e, "postalCode");
                            }}
                            value={selectedPostCode}
                            labelLeftPadding="16px"
                            msgCustomStyles={{ width: "auto" }}
                            brand={
                              application === "renewal" ? brand : undefined
                            }
                          />
                        </InputValidationWrapper>
                      </button>
                      {searchAddressByPostCodeEnabled && (
                        <div className=" flex mt-spacing-xs">
                          <Button
                            id="searchByPostCodeButton"
                            btnType="label"
                            type="button"
                            label={props?.searchByPostCodeButton ?? ""}
                            className={`${getTextColorClasses(
                              brand
                            )} text-2xs font-semibold font-['Proxima Nova'] underline leading-normal`}
                            onClick={() => handleSearchByPostCode()}
                            isOnlyActiveOnPress
                          />
                        </div>
                      )}
                    </TransformAnimation>
                  </div>
                )}
              </div>
            )}

            {application === "quote" &&
              isShowNationalityDropDown &&
              renderNationality()}

            {application === "quote" && renderDOB()}

            {/* IDENTIFICATION FIELDS (Spain only) - Hide for Spain hub variant */}
            {application === "quote" &&
              showIdentificationFields &&
              !isPersonalDetailsDisabled &&
              renderIdentificationFields()}

            <div
              id="action-button-id"
              className="md:mt-spacing-xxl mt-spacing-l text-left"
            >
              <h1 className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center md:mb-spacing-l mb-spacing-m">
                {questionTwoText}
              </h1>
              <div
                className={`${Styles.traveller_btn_wrapper}`}
                ref={
                  errorDetails.firstError === "travelling"
                    ? firstErrorRef
                    : undefined
                }
              >
                <FadeAnimation>
                  <ButtonGroup
                    setSelectedAnswer={(answer: number) => {
                      handleIsPersonTravelling(answer);
                      setFocusedInputs(getFocusedFields("travelling"));
                    }}
                    selectedAnswer={
                      findAnswerQThreeByValue(formData.travelling, t)?.id
                    }
                    answerArray={answersQuestionYesNo(t)}
                    location={QuestionThree}
                    type="organiser-main"
                    onOptionClick={(id, dataTestId) => {
                      const buttonElement = document.querySelector(
                        `#${dataTestId}`
                      );

                      if (
                        !buttonElement?.classList.contains(
                          "componnt_lib_toggle_btn_inactive"
                        )
                      ) {
                        return;
                      }

                      let action;
                      let delay = 150;
                      const element1 =
                        document.getElementById("paragaph-test-id");
                      if (
                        id === 2 ||
                        !(newMedicalFlowEnabled && travellerType === Individual)
                      ) {
                        action = () => {
                          if (element1) {
                            organiserMedscrollToAnswerWithFooter(
                              isStickyFooter,
                              "organiser"
                            );
                          } else {
                            organiserMedscrollToAnswer(
                              isStickyFooter,
                              "organiser"
                            );
                          }
                        };
                      } else {
                        action = () => {
                          let scrollBlock: ScrollLogicalPosition = "start";

                          if (element1) {
                            scrollBlock = !isStickyFooter ? "center" : "end";
                          }

                          medicalAnswerRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: scrollBlock,
                          });
                        };
                        delay = 200;
                      }

                      setTimeout(action, delay);
                    }}
                  />
                </FadeAnimation>
              </div>
            </div>

            {newMedicalFlowEnabled && travellerType === Individual && (
              <div ref={medicalAnswerRef}>
                <div
                  ref={
                    errorDetails.firstError === "hasMedicalConditions"
                      ? firstErrorRef
                      : undefined
                  }
                />
                <TravellerMedicalAnswer
                  show={formData.travelling}
                  questionText={medicalQuestionText}
                  questionInfoText={medicalQuestionInfoText}
                  serviceDeniedText={medicalServiceDeniedText}
                  yesBtnText={medicalYesBtnText}
                  noBtnText={medicalNoBtnText}
                  location={QuestionThree}
                  selectedValue={formData?.hasMedicalConditions}
                  onChange={handleMedicalQuestion}
                  type="organiser"
                />
              </div>
            )}
            {showErrors &&
              errorDetails.count > 0 &&
              formData?.hasMedicalConditions !== true && (
                <div className="mt-spacing-l">
                  <InlineInfoBox
                    id="traveler-form-inline-info-box"
                    content={
                      <p className="text-text-info-1-primary">
                        {getAlertMessageIncompleteSection()}
                        &nbsp;
                        <button
                          type="button"
                          className="underline font-bold leading-[24px]"
                          onClick={() => {
                            const el = firstErrorRef?.current as Element | null;
                            if (el) {
                              const topOffset =
                                el.getBoundingClientRect().top +
                                window.scrollY -
                                20;
                              window.scrollTo({
                                top: topOffset,
                                behavior: "smooth",
                              });
                            }
                          }}
                        >
                          {getAlertMessageLinkSection()}
                        </button>
                      </p>
                    }
                    style={{
                      width: "100%",
                      backgroundColor: "#FFF0E9",
                      borderColor: "#D06D10",
                      padding: "8px 16px 8px 16px",
                      borderRadius: "8px",
                      color: "#272427",
                      fontSize: "16px",
                      lineHeight: "24px",
                    }}
                  />
                </div>
              )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default withBrand(withApplication(OrganiserForm));
